// @section 1 VoiceRecorder — browser-native speech-to-text (Web Speech API).
//
// Free, no API key, no server round-trip: the browser streams the mic to a
// SpeechRecognition engine (Chrome/Edge) and returns interim + final text.
// Used by the Target Firms "New Target" form to dictate firm/vertical/business
// model. If the browser does not support it (or the mic is denied) the component
// renders a disabled note and the parent form stays fully usable by typing.

import { useCallback, useEffect, useRef, useState } from 'react';

// @subsection 1.1 Minimal Web Speech API typings (not in the standard TS lib).
type SpeechRecognitionResultLike = {
  0: { transcript: string };
  isFinal: boolean;
};
type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: { length: number; [i: number]: SpeechRecognitionResultLike };
};
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const MAX_SECONDS = 90;

// @section 2 Component
export default function VoiceRecorder({
  onTranscript,
}: {
  // Called with the full transcript text when recording stops.
  onTranscript: (text: string) => void;
}) {
  const [supported] = useState(() => getRecognitionCtor() !== null);
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recogRef = useRef<SpeechRecognitionLike | null>(null);
  const finalRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // @block 2.1 Stop + cleanup.
  const stop = useCallback(() => {
    recogRef.current?.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  }, []);

  useEffect(() => () => { recogRef.current?.stop(); if (timerRef.current) clearInterval(timerRef.current); }, []);

  // @block 2.2 Start recording.
  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) { setError('Voice capture is not supported in this browser.'); return; }
    setError(null);
    setInterim('');
    finalRef.current = '';
    setSeconds(0);

    const recog = new Ctor();
    recog.lang = 'en-US';
    recog.continuous = true;
    recog.interimResults = true;

    recog.onresult = (e) => {
      let live = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalRef.current += r[0].transcript;
        else live += r[0].transcript;
      }
      setInterim(live);
    };
    recog.onerror = (e) => {
      setError(e.error === 'not-allowed'
        ? 'Microphone permission denied. Type the details instead.'
        : `Voice capture error: ${e.error}`);
      stop();
    };
    recog.onend = () => {
      const text = (finalRef.current + ' ' + interim).trim();
      if (text) onTranscript(text);
      setInterim('');
    };

    recogRef.current = recog;
    try { recog.start(); } catch { /* already started */ }
    setRecording(true);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) { stop(); return MAX_SECONDS; }
        return s + 1;
      });
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTranscript, stop]);

  if (!supported) {
    return (
      <div style={{ fontSize: 12, color: 'var(--tx-4)' }}>
        Voice capture needs Chrome or Edge. Type the details below.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={recording ? stop : start}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
          border: '1px solid var(--line)', cursor: 'pointer',
          background: recording ? 'var(--neg)' : 'var(--brand-3)',
          color: recording ? '#fff' : 'var(--bg-0, #0b0b0c)',
        }}
      >
        <span style={{ fontSize: 14 }}>{recording ? '■' : '●'}</span>
        {recording ? `Stop (${seconds}s)` : 'Record memo'}
      </button>
      {recording && (
        <span style={{ fontSize: 12, color: 'var(--tx-3)', maxWidth: 360 }}>
          {interim || 'Listening…'}
        </span>
      )}
      {error && <span style={{ fontSize: 12, color: 'var(--warn)' }}>{error}</span>}
    </div>
  );
}
